import React, { useCallback, useEffect, useRef, useState } from "react";
import { dapi as api } from "./deskApi";
import { Plus, ListChecks, FileText, Trash2, X } from "lucide-react";

/** Notepad: plain text notes + "things to do" checklists, auto-saved. */
export default function NotepadApp() {
  const [notes, setNotes] = useState(null);
  const [sel, setSel] = useState(null);
  const [newItem, setNewItem] = useState("");
  const timer = useRef(null);
  const pending = useRef({});

  const load = useCallback(() => {
    api.get("/desktop/notes").then((r) => {
      setNotes(r.data);
      setSel((s) => s || r.data[0]?.id || null);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const note = (notes || []).find((n) => n.id === sel);

  const createNote = async (kind) => {
    const { data } = await api.post("/desktop/notes", { kind });
    setNotes((p) => [data, ...(p || [])]);
    setSel(data.id);
  };

  const mutate = (id, sets) => {
    setNotes((p) => p.map((n) => (n.id === id ? { ...n, ...sets } : n)));
    pending.current = { id, sets: { ...(pending.current.id === id ? pending.current.sets : {}), ...sets } };
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const p = pending.current;
      pending.current = {};
      if (p.id) api.patch(`/desktop/notes/${p.id}`, p.sets).catch(() => {});
    }, 600);
  };

  const removeNote = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    await api.delete(`/desktop/notes/${n.id}`).catch(() => {});
    setNotes((p) => {
      const left = p.filter((x) => x.id !== n.id);
      setSel(left[0]?.id || null);
      return left;
    });
  };

  const addItem = () => {
    const text = newItem.trim();
    if (!text || !note) return;
    mutate(note.id, { items: [...(note.items || []), { text, done: false }] });
    setNewItem("");
  };
  const toggleItem = (i) => {
    const items = (note.items || []).map((it, j) => (j === i ? { ...it, done: !it.done } : it));
    mutate(note.id, { items });
  };
  const editItem = (i, text) => {
    const items = (note.items || []).map((it, j) => (j === i ? { ...it, text } : it));
    mutate(note.id, { items });
  };
  const removeItem = (i) => {
    mutate(note.id, { items: (note.items || []).filter((_, j) => j !== i) });
  };

  const doneCount = note?.kind === "todo" ? (note.items || []).filter((i) => i.done).length : 0;

  return (
    <div className="h-full flex text-slate-800" data-testid="notepad-app">
      {/* sidebar */}
      <div className="w-44 border-r border-slate-200 bg-slate-50 flex flex-col shrink-0">
        <div className="flex gap-1 p-2 border-b border-slate-200">
          <button type="button" onClick={() => createNote("text")}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-bold px-1.5 py-1.5 rounded bg-white border border-slate-200 hover:bg-slate-100"
            title="New text note" data-testid="notepad-new-note">
            <Plus className="w-3 h-3" /> Note
          </button>
          <button type="button" onClick={() => createNote("todo")}
            className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] font-bold px-1.5 py-1.5 rounded bg-white border border-slate-200 hover:bg-slate-100"
            title="New to-do checklist" data-testid="notepad-new-todo">
            <Plus className="w-3 h-3" /> To-do
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {notes && notes.length === 0 && (
            <p className="text-[11px] text-slate-400 text-center mt-6 px-3">No notes yet — create one above.</p>
          )}
          {(notes || []).map((n) => (
            <button key={n.id} type="button" onClick={() => setSel(n.id)}
              className={`w-full flex items-center gap-1.5 px-2.5 py-2 text-left text-xs border-b border-slate-100 ${sel === n.id ? "bg-sky-100 font-semibold" : "hover:bg-slate-100"}`}
              data-testid={`notepad-note-${n.id}`}>
              {n.kind === "todo"
                ? <ListChecks className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                : <FileText className="w-3.5 h-3.5 text-sky-600 shrink-0" />}
              <span className="truncate">{n.title || "Untitled"}</span>
            </button>
          ))}
        </div>
      </div>

      {/* editor */}
      {!note ? (
        <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
          Create a note or a to-do list to get started.
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200">
            <input
              value={note.title}
              onChange={(e) => mutate(note.id, { title: e.target.value })}
              className="flex-1 min-w-0 font-bold text-sm bg-transparent outline-none"
              placeholder="Title"
              data-testid="notepad-title"
            />
            {note.kind === "todo" && (
              <span className="text-[11px] text-slate-400 whitespace-nowrap" data-testid="notepad-progress">
                {doneCount}/{(note.items || []).length} done
              </span>
            )}
            <button type="button" className="p-1.5 rounded hover:bg-red-50 text-red-500" onClick={() => removeNote(note)}
              title="Delete this note" data-testid="notepad-delete">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {note.kind === "text" ? (
            <textarea
              value={note.content || ""}
              onChange={(e) => mutate(note.id, { content: e.target.value })}
              className="flex-1 w-full resize-none p-3 text-sm outline-none font-mono leading-relaxed"
              placeholder="Type your note…"
              data-testid="notepad-content"
            />
          ) : (
            <div className="flex-1 overflow-auto p-2">
              {(note.items || []).map((it, i) => (
                <div key={i} className="group flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50" data-testid={`todo-item-${i}`}>
                  <input type="checkbox" checked={!!it.done} onChange={() => toggleItem(i)}
                    className="w-4 h-4 accent-emerald-600 shrink-0 cursor-pointer" data-testid={`todo-check-${i}`} />
                  <input
                    value={it.text}
                    onChange={(e) => editItem(i, e.target.value)}
                    className={`flex-1 min-w-0 text-sm bg-transparent outline-none ${it.done ? "line-through text-slate-400" : ""}`}
                    data-testid={`todo-text-${i}`}
                  />
                  <button type="button" onClick={() => removeItem(i)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 text-red-400"
                    title="Remove item" data-testid={`todo-remove-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2 px-1.5 py-1.5 mt-1">
                <Plus className="w-4 h-4 text-slate-300 shrink-0" />
                <input
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }}
                  onBlur={addItem}
                  placeholder="Add a thing to do — press Enter"
                  className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-300"
                  data-testid="todo-new-item"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
